import type SwiperCore from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';
import { A11y, Zoom } from 'swiper/modules';
import "swiper/swiper-bundle.css?__remix_sideEffect__";
import { useCallback, useEffect, useState } from 'react';
import { cn } from '~/utils/misc.tsx';
import { type SwiperOptions } from 'swiper/types';
import { Icon } from '~/components/ui/icons/icon.tsx';

type Props = {
  slides: any[];
  initialIndex?: number; // Make initialIndex optional
  slidesPerView: number;
  paginationContainerClassName?: string;
  className?: string;
  withPagination?: boolean;
  withDesktopNavigation?: boolean;
  spaceBetween?: number;
  loop?: boolean;
  breakpoints?: SwiperOptions['breakpoints'];
  swiper?: SwiperCore;
  setSwiper?: (swiper: SwiperCore) => void; // Add setSwiper prop
  activeIndex?: number;
  onActiveIndexChange?: (index: number) => void;
  zoom?: boolean;
};

export const Carousel = ({
  initialIndex = 0, // Set default value to 0
  loop,
  slides,
  spaceBetween,
  slidesPerView = 1,
  paginationContainerClassName,
  withPagination,
  withDesktopNavigation = false,
  className,
  breakpoints,
  swiper: externalSwiper,
  setSwiper, // Add setSwiper to destructured props
  activeIndex: controlledActiveIndex,
  onActiveIndexChange,
  zoom = false
}: Props) => {
  const [internalSwiper, setInternalSwiper] = useState<SwiperCore | undefined>(
    undefined
  );
  const [internalActiveIndex, setInternalActiveIndex] = useState(initialIndex);
  const [carouselLocked, setCarouselLocked] = useState(true);

  // Use controlled or internal state
  const activeIndex =
  controlledActiveIndex !== undefined ?
  controlledActiveIndex :
  internalActiveIndex;

  const swiper = externalSwiper || internalSwiper;

  useEffect(() => {
    if (externalSwiper) {
      setInternalActiveIndex(externalSwiper.realIndex);
    }
  }, [externalSwiper]);

  const handleActiveIndexChange = (index: number) => {
    setInternalActiveIndex(index);
    onActiveIndexChange?.(index);
  };

  const handlePaginationClick = (index: number) => {
    if (loop) {
      swiper?.slideToLoop(index);
    } else {
      swiper?.slideTo(index);
    }
    handleActiveIndexChange(index);
  };

  const syncCarouselLocked = useCallback((instance: SwiperCore) => {
    setCarouselLocked(!!instance.isLocked);
  }, []);

  const showDesktopNav =
  withDesktopNavigation && slides.length > 0 && swiper && !carouselLocked;

  const atStart = swiper?.isBeginning ?? true;
  const atEnd = swiper?.isEnd ?? true;
  const prevDisabled = !loop && atStart;
  const nextDisabled = !loop && atEnd;

  return (
    <div
      className={cn(
        withDesktopNavigation &&
        'relative isolate overflow-visible px-2 sm:px-3'
      )}>

      <Swiper
        zoom={zoom}
        initialSlide={initialIndex}
        spaceBetween={spaceBetween}
        className={cn(className, withDesktopNavigation && 'relative z-0')}
        modules={[A11y, Zoom]}
        breakpoints={breakpoints}
        slidesPerView={slidesPerView}
        onSwiper={(newSwiper) => {
          if (setSwiper) {
            setSwiper(newSwiper);
          } else {
            setInternalSwiper(newSwiper);
          }
          syncCarouselLocked(newSwiper);
        }}
        onResize={syncCarouselLocked}
        onBreakpoint={syncCarouselLocked}
        loop={loop}
        onSlideChange={(changedSwiper) => {
          handleActiveIndexChange(changedSwiper.realIndex);
          syncCarouselLocked(changedSwiper);
        }}>

        {slides.map((slide, index) =>
        <SwiperSlide zoom={zoom} key={index}>
            {slide}
          </SwiperSlide>
        )}
        {/* Pagination */}
        {withPagination &&
        <div
          slot="container-end"
          className={cn(
            'mt-9 flex justify-center gap-2',
            paginationContainerClassName
          )}>

            {slides.map((_, index) =>
          <div
            key={index}
            className="flex h-[24px] cursor-pointer items-center"
            onClick={() => handlePaginationClick(index)}>

                <button
              key={index}
              className={`
                      h-[10px] w-[10px] min-w-[10px] rounded-full
    
                      ${
              activeIndex === index ?
              'bg-secondary-500' :
              'bg-gray-300'
              }
                    `}>
            </button>
              </div>
          )}
          </div>}

      </Swiper>

      {showDesktopNav &&
      <>
          <button
          type="button"
          aria-label="Previous slide"
          disabled={prevDisabled}
          onClick={() => swiper.slidePrev()}
          className={cn(
            'hover:bg-secondary-50 absolute left-0 top-1/2 z-30 hidden h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full border border-input bg-white text-secondary shadow-md transition disabled:pointer-events-none disabled:opacity-40 lg:flex'
          )}>

            <Icon
            name="chevron-down"
            size="drawer"
            className="rotate-90 text-current" />

          </button>
          <button
          type="button"
          aria-label="Next slide"
          disabled={nextDisabled}
          onClick={() => swiper.slideNext()}
          className={cn(
            'hover:bg-secondary-50 absolute right-0 top-1/2 z-30 hidden h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full border border-input bg-white text-secondary shadow-md transition disabled:pointer-events-none disabled:opacity-40 lg:flex'
          )}>

            <Icon
            name="chevron-down"
            size="drawer"
            className="-rotate-90 text-current" />

          </button>
        </>}

    </div>);

};